import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { PostComposer } from '@/components/PostComposer'
import { PostCard } from '@/components/PostCard'
import { postsApi, FeedItem } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'

export function FeedPage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: feedData, isLoading } = useQuery({
    queryKey: ['feed'],
    queryFn: () => postsApi.getFeed(),
  })

  const likeMutation = useMutation({
    mutationFn: async (postId: string) => {
      console.log('Like mutation called for post:', postId)
      try {
        const result = await postsApi.likePost(postId)
        console.log('Like mutation success:', result)
        return result
      } catch (error) {
        console.error('Like mutation error:', error)
        throw error
      }
    },
    onMutate: async (postId: string) => {
      console.log('Like mutation onMutate:', postId)
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['feed'] })

      // Snapshot the previous value
      const previousFeed = queryClient.getQueryData(['feed'])

      // Optimistically update to the new value
      queryClient.setQueryData(['feed'], (old: any) => {
        if (!old?.data) return old
        return {
          ...old,
          data: old.data.map((item: FeedItem) =>
            item.post.post.id === postId
              ? {
                  ...item,
                  post: {
                    ...item.post,
                    like_count: item.post.like_count + 1,
                    liked_by_me: true,
                  },
                  user_reaction: 'like',
                }
              : item
          ),
        }
      })

      return { previousFeed }
    },
    onError: (err, postId, context) => {
      console.error('Like mutation onError:', { err, postId, context })
      // Rollback on error
      if (context?.previousFeed) {
        queryClient.setQueryData(['feed'], context.previousFeed)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] })
    },
  })

  const unlikeMutation = useMutation({
    mutationFn: async (postId: string) => {
      console.log('Unlike mutation called for post:', postId)
      try {
        const result = await postsApi.unlikePost(postId)
        console.log('Unlike mutation success:', result)
        return result
      } catch (error) {
        console.error('Unlike mutation error:', error)
        throw error
      }
    },
    onMutate: async (postId: string) => {
      console.log('Unlike mutation onMutate:', postId)
      await queryClient.cancelQueries({ queryKey: ['feed'] })
      const previousFeed = queryClient.getQueryData(['feed'])

      queryClient.setQueryData(['feed'], (old: any) => {
        if (!old?.data) return old
        return {
          ...old,
          data: old.data.map((item: FeedItem) =>
            item.post.post.id === postId
              ? {
                  ...item,
                  post: {
                    ...item.post,
                    like_count: Math.max(0, item.post.like_count - 1),
                    liked_by_me: false,
                  },
                  user_reaction: null,
                }
              : item
          ),
        }
      })

      return { previousFeed }
    },
    onError: (err, postId, context) => {
      console.error('Unlike mutation onError:', { err, postId, context })
      if (context?.previousFeed) {
        queryClient.setQueryData(['feed'], context.previousFeed)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] })
    },
  })

  const handleReact = (postId: string, isLiked: boolean) => {
    if (isLiked) {
      unlikeMutation.mutate(postId)
    } else {
      likeMutation.mutate(postId)
    }
  }

  const handleDelete = (postId: string) => {
    // Optimistically remove from feed
    queryClient.setQueryData(['feed'], (old: any) => {
      if (!old?.data) return old
      return {
        ...old,
        data: old.data.filter((item: FeedItem) => item.post.post.id !== postId),
      }
    })
  }

  const handleEdit = (postId: string, content: string, mediaIds: string[]) => {
    // The edit modal already handles invalidation, but we can add optimistic update here if needed
    queryClient.invalidateQueries({ queryKey: ['feed'] })
  }

  return (
    <div className="space-y-6">
      {/* Create post card */}
      <PostComposer user={user} />

      {/* Feed posts */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : !feedData?.data || feedData.data.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <h3 className="font-display text-lg font-semibold">Your feed is empty</h3>
            <p className="mt-2 text-muted-foreground">
              Start following people to see their posts here
            </p>
            <Button className="mt-4" asChild>
              <a href="/explore">Explore</a>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {(feedData.data as FeedItem[]).map((item: FeedItem, index: number) => (
            <motion.div
              key={item.post.post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <PostCard
                item={item}
                onReact={handleReact}
                currentUser={user}
                onDelete={handleDelete}
                onEdit={handleEdit}
              />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  )
}
